import { useTranslations } from "next-intl";
import { Crown, Sparkles } from "lucide-react";
import { Card } from "@chotca/ui";
import { nextPlanUp, type PlanCode } from "@chotca/config";
import { usePlanStatus, usePlanCatalog } from "@chotca/api-client";
import { useSubscriptionModals } from "./subscription-modals";

// Home card that surfaces the org's current plan tier + expiry + quota, with a
// primary CTA to upgrade (or renew on the top tier). Free orgs get an upsell
// line; Pro/Business see their remaining allowances and days left.
export function PlanStatusCard({ orgId }: { orgId: string }) {
  const t = useTranslations("dashboard");
  const { data, isLoading } = usePlanStatus(orgId);
  const catalog = usePlanCatalog(orgId);
  const { upgradeTo } = useSubscriptionModals();

  const code = (data?.effectiveCode ?? "FREE") as PlanCode;
  const isFree = code === "FREE";
  const planName = data?.plan.name ?? t("planCardFree");
  const next = nextPlanUp(code);
  const days = data?.daysUntilExpiry ?? null;

  // Live spec of the next tier up (for the free-user upsell). Sourced from the
  // catalog so admin quota edits show without a deploy.
  const nextPlanRow = next
    ? catalog.data?.items.find((p) => p.code === next.code)
    : undefined;

  return (
    <Card tone="paper" bordered className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span
              className={
                isFree
                  ? "inline-flex items-center gap-1 rounded-full bg-mist px-2.5 py-0.5 text-xs font-bold text-slate"
                  : "inline-flex items-center gap-1 rounded-full bg-ink px-2.5 py-0.5 text-xs font-bold text-paper"
              }
            >
              {isFree ? <Sparkles size={12} /> : <Crown size={12} />}
              {planName}
            </span>
            {!isFree && (
              <span className="text-body-xs text-ash">
                {days != null
                  ? t("planCardExpiry", { days: Math.max(0, days) })
                  : t("planCardExpiryNone")}
              </span>
            )}
          </div>

          {isFree ? (
            nextPlanRow && next ? (
              // Upsell — kept punchy: the numbers are bold/inked so they stand
              // out from the muted surrounding copy.
              <p className="text-body-sm leading-relaxed text-ink">
                {t.rich("planCardUpsellRich", {
                  nextPlan: next.label,
                  sourcing: nextPlanRow.sourcingQuota,
                  postings: nextPlanRow.maxConcurrentPostings,
                  boost: nextPlanRow.boostQuota,
                  b: (chunks) => (
                    <span className="mx-0.5 inline-block rounded-md bg-lime-light px-1.5 py-0.5 font-bold text-ink">
                      {chunks}
                    </span>
                  ),
                })}
              </p>
            ) : (
              <p className="text-body-sm text-ash">{t("planCardFreeDesc")}</p>
            )
          ) : (
            <p className="text-body-sm text-ash">
              {t("planCardQuotaSourcing", {
                count: data?.sourcingRemaining ?? 0,
              })}
              {" · "}
              {t("planCardQuotaPostings", {
                count: data?.plan.maxConcurrentPostings ?? 1,
              })}
              {" · "}
              {t("planCardQuotaBoost", { count: data?.boostRemaining ?? 0 })}
            </p>
          )}
        </div>

        {/* CTA: the button names the exact next tier ("Nâng cấp Pro"), so go
            straight to that plan (with a confirm) instead of re-opening a
            chooser. Free→Pro, Pro→Business; Business (no next) → renew. */}
        {next ? (
          <button
            type="button"
            disabled={isLoading}
            onClick={() => upgradeTo(next.code)}
            className="shrink-0 rounded-full bg-lime px-4 py-2 text-sm font-bold text-ink transition hover:brightness-95 disabled:opacity-60"
          >
            {t("planCardUpgradeCta", { nextPlan: next.label })}
          </button>
        ) : (
          <button
            type="button"
            disabled={isLoading}
            onClick={() => upgradeTo(code)}
            className="shrink-0 rounded-full border border-line px-4 py-2 text-sm font-bold text-ink transition hover:bg-mist disabled:opacity-60"
          >
            {t("planCardRenewCta")}
          </button>
        )}
      </div>
    </Card>
  );
}
